import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import * as _ from 'underscore';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, OperationType } from '../../../models/enums';
import { KeyFileType, KeyFileViewModel } from '../../../models/key-file';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { NameDescriptionComponent } from '../../name-description/name-description.component';

@Component({
    standalone: false,
    selector: 'res-key-file-modal',
    templateUrl: './key-file-modal.component.html',
    styleUrls: ['./key-file-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class KeyFileModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild('lotusFileUpload') lotusFileUpload: ElementRef;
    @ViewChild('bitlockerFileUpload') bitlockerFileUpload: ElementRef;

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (name) => {
            return {
                name: this.nameDescriptionComponent.name,
                objectType: KeyFileType.LOTUS_NOTES,
                projectHandle: this.commonService.currentOrganizationHandle
            };
        }
    };

    modalTitle = '';
    modalAutomationId = 'key-file-template';
    secondaryButtonLabel = 'Cancel';
    primaryButtonLabel = 'OK';
    verifyRequiredValidator = true;
    isDescriptionHidden = true;
    isBusy = false;
    isBusyMessage = '';
    modalHelpIndex = HelpIndex.add_edit_container_key_file_dlg;
    modalClass = 'key-file-modal';
    handle = '';

    lotusNotesEncodedData: any;
    bitLockerEncodedData: any;
    lotusNotesPassword: string;
    keyFileTypes = {
        New: {
            title: 'New Key File',
            isBusyMessage: 'Creating a new Key File',
            endPoint: Endpoint.orgManager_createLotusNotesIdFile
        },
        Edit: {
            title: 'Edit Key File',
            isBusyMessage: 'Editing Key File',
            endPoint: Endpoint.orgManager_updateLotusNotesIdFile
        },
    };
    currentKeyFile: any;
    keyFileOperationType: OperationType;
    @Input() keyFileTypeSelected = KeyFileType.LOTUS_NOTES;
    public lotusNotesFile: any;
    public bitLockerFile: any

    constructor(
        private modalService: BsModalService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private cdRef: ChangeDetectorRef
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.nameDescriptionComponent.drObject = new KeyFileViewModel();
        this.keyFileOperationType = <OperationType>this.modalData.operationType;
        this.currentKeyFile = _.pick(this.keyFileTypes, this.keyFileOperationType);
        this.modalTitle = this.currentKeyFile[Object.keys(this.currentKeyFile)[0]].title;

        if (this.keyFileOperationType === OperationType.edit) {
            this.nameDescriptionComponent.name = this.modalData.data.name;
            this.nameDescriptionComponent.description = this.modalData.data.description;
            this.keyFileTypeSelected = this.modalData.data.type;
            if (this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES) {
                this.lotusNotesFile = this.modalData.data.fileName ? new File([''], this.modalData.data.fileName, { type: 'text/plain' }) : null;
            } else {
                this.bitLockerFile = this.modalData.data.fileName ? new File([''], this.modalData.data.fileName, { type: 'text/plain' }) : null;
            }
        }
        this.cdRef.detectChanges();
    }

    public onAccept(file: any): void {
        if (this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES) {
            this.lotusNotesFile = file?.target?.files[0];
        } else {
            if (file?.target?.files?.length > 0) {
                this.bitLockerFile = file?.target?.files[0];
            }
        }
        if (file?.target?.files?.length > 0) {
            this.handleInputFile(file?.target?.files[0]);
        }
    }

    public onRemove(file: any): void {
        if (this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES) {
            this.lotusNotesFile = null;
            this.lotusFileUpload.nativeElement.value = '';
        } else {
            this.bitLockerFile = null;
            this.bitlockerFileUpload.nativeElement.value = '';
        }
        if (file?.target?.files?.length > 0) {
            this.handleInputFile(file?.target?.files[0]);
        }
    }

    handleInputFile(file): void {
        if (file) {
            const reader: any = new FileReader();
            reader.onload = (readerEvt): any => {
                let binaryString = '';

                if (this.commonService.currentBrowserIsIE) {
                    binaryString = this.commonService.convertArrayToBinaryString(reader.result);
                    reader.content = binaryString;
                } else {
                    binaryString = readerEvt.target.result;
                }

                if (this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES) {
                    this.lotusNotesEncodedData = btoa(binaryString);
                } else {
                    this.bitLockerEncodedData = btoa(binaryString);
                }
            };
            if (this.commonService.currentBrowserIsIE) {
                reader.readAsArrayBuffer(file);
            } else {
                reader.readAsBinaryString(file);
            }

            reader.onerror = function (event) {
                console.error('File could not be read! Code ' + event.target.error.code);
            };
        }
    }

    OK(): void {
        const request = {
            name: this.nameDescriptionComponent.name,
            // description: this.nameDescriptionComponent.description, // TODO - ST changes needed -    ECASL-26924 and ECASL-27567
            fileName: this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES ? this.lotusNotesFile?.name : this.bitLockerFile?.name
        };

        this.isBusy = true;
        this.isBusyMessage = this.currentKeyFile[Object.keys(this.currentKeyFile)[0]].isBusyMessage;

        if (this.currentKeyFile[Object.keys(this.currentKeyFile)[0]].title === 'Edit Key File') {
            request['handle'] = this.modalData.data.handle;
        } else {
            request['idFileBytes'] = this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES ? this.lotusNotesEncodedData : this.bitLockerEncodedData;
            request['type'] = this.keyFileTypeSelected;
            request['filePassword'] = this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES ? this.lotusNotesPassword : null;
        }

        // make api call based on the keyFile operation
        this.restService.post(this.currentKeyFile[Object.keys(this.currentKeyFile)[0]].endPoint,
            request
        ).then(response => {
            this.isBusy = false;
            this.closeModal(true);
        }, error => {
            this.isBusy = false;
        });

    }

    isPrimaryButtonEnabled(): boolean {
        if (this.keyFileOperationType === OperationType.edit) {
            return this.nameDescriptionComponent.formValid && !this.isBusy;
        } else {
            return this.keyFileTypeSelected === KeyFileType.LOTUS_NOTES ? this.nameDescriptionComponent.formValid && this.lotusNotesFile?.name && !this.isBusy :
                this.nameDescriptionComponent.formValid && this.bitLockerFile?.name && !this.isBusy;
        }
    }
}
